import { isEqual } from "underscore";

import {
  DynamicDocumentItemValue,
  DocumentProgress,
  DynamicDocumentItem,
  DynamicDocumentItemData,
  FormattedDocumentItem,
  HighLow,
  InstallationModuleData,
  SerialNumber
} from "@/types";
import SerialnumberItem from "@/containers/serialnumbers-manager/SerialnumberItem.vue";

export function item(
  item: DynamicDocumentItem,
  data: DynamicDocumentItemData,
  moduleItemData: InstallationModuleData[keyof InstallationModuleData]
): FormattedDocumentItem[] {
  if (!data) return [];

  switch (item.type) {
    case "BOOL":
      return [
        {
          type: "text",
          title: item.title,
          value: (data.value === true && "Ja") || (data.value === false && "Nee") || "Niet ingevuld"
        }
      ];
    case "IMAGE": {
      const value = data.value as string[];

      if (!value || !value.length) {
        return [{ type: "text", title: item.title, value: "Geen afbeeldingen toegevoegd" }];
      }

      return value.map((image, index) => ({
        type: "image",
        title: `${item.title} (${index + 1}/${value.length})`,
        value: image
      }));
    }
    case "MULTIPLE_CHOICE": {
      const value = data.value as string | string[];

      if (!value?.length) return [{ type: "text", title: item.title, value: "Niet ingevuld" }];

      if (typeof value === "string") return [{ type: "text", title: item.title, value }];

      return [
        {
          type: "text",
          title: item.title,
          value: `<ul>${value.map(val => `<li>${val}</li>`).join("")}</ul>`
        }
      ];
    }
    case "SIGNATURE": {
      const value = data.value as string[];

      if (!value || !value.length) {
        return [{ type: "text", title: item.title, value: "Geen handtekening gezet" }];
      }

      return [
        { type: "text", title: "Ondertekend door", value: value[0] || "Niet ingevuld" },
        { type: "image", title: "Handtekening", value: value[1] || "Niet ingevuld" }
      ];
    }
    case "HIGH-LOW": {
      const value = data.value as HighLow;

      return [
        {
          type: "text",
          title: `${item.title} (min)`,
          value: (value && `${value[0]} (${item.meta?.unit})`) || "Niet ingevuld"
        },
        {
          type: "text",
          title: `${item.title} (max)`,
          value: (value && `${value[1]} (${item.meta?.unit})`) || "Niet ingevuld"
        }
      ];
    }
    case "CUSTOM": {
      const serialNumberFields = [
        "meterSerialNumber",
        "meterSerialNumbersEnphaseGateway",
        "meterSerialNumbersEnphaseRelay",
        "modemSerialNumber",
        "dataloggerSerialNumber",
        "dataloggerCcSerialNumber",
        "communicationModuleSerialNumber",
        "batterySerialNumber",
        "kwhMeterSerialNumber",
        "inverterSerialNumbers"
      ];

      if (serialNumberFields.includes(item.field as string)) {
        let formattedData;
        if (Array.isArray(data.value)) {
          formattedData = (data.value as SerialNumber[])
            .map(sn => {
              return (sn.serialNumber || "").trim();
            })
            .join(", ");
        }

        if (!formattedData && Array.isArray(moduleItemData)) {
          const isSerialNumberArray =
            moduleItemData.length === 0 ||
            (moduleItemData.length > 0 && "serialNumber" in moduleItemData[0]);

          if (isSerialNumberArray) {
            formattedData = moduleItemData.map(sn => (sn.serialNumber || "").trim()).join(", ");
          }
        }

        return [
          {
            type: "text",
            title: item.title || item.field,
            value: formattedData || "Niet ingevuld"
          }
        ];
      }

      return [];
    }
    case "WARNING":
    case "HEADER":
      return [];
    case "NUMBER": {
      const value = data.value as number;

      return [
        {
          type: "text",
          title: item.title,
          value:
            typeof value === "number" && !Number.isNaN(value)
              ? value.toLocaleString("nl-NL")
              : "Niet ingevuld"
        }
      ];
    }
    default: {
      const value = data.value as string;

      return [{ type: "text", title: item.title, value: value || "Niet ingevuld" }];
    }
  }
}

export function getDefaultValue(item: DynamicDocumentItem): DynamicDocumentItemValue {
  switch (item.type) {
    case "BOOL":
      return item.defaultValue !== undefined ? item.defaultValue : null;
    case "IMAGE":
      return [];
    case "MULTIPLE_CHOICE":
      return item.meta?.multiple ? [] : "";
    case "SIGNATURE":
    case "HIGH-LOW":
      return [];
    case "SERIAL":
      return [{ serialNumber: "", source: "manual" }];
    case "TEXT":
    case "EMAIL":
    case "VALUE":
    case "NUMBER":
      return "";
    case "DATE":
      return new Date().toISOString().substr(0, 10);
    case "CUSTOM": {
      const serialNumberFields = [
        "meterSerialNumber",
        "meterSerialNumbersEnphaseGateway",
        "meterSerialNumbersEnphaseRelay",
        "modemSerialNumber",
        "dataloggerSerialNumber",
        "dataloggerCcSerialNumber",
        "communicationModuleSerialNumber",
        "batterySerialNumber",
        "kwhMeterSerialNumber",
        "inverterSerialNumbers"
      ];

      if (serialNumberFields.includes(item.field as string)) {
        return [{ serialNumber: "", source: "manual" }];
      }
      return undefined;
    }
    default:
      return undefined;
  }
}

export function areEqual(a?: DynamicDocumentItemData, b?: DynamicDocumentItemData) {
  return isEqual(a?.value, b?.value);
}

export function progress(
  items: DynamicDocumentItem[],
  data: DynamicDocumentItemData[]
): DocumentProgress {
  const readyItems = data.filter(item => item.isValid);

  return {
    required: {
      total: items.filter(i => i.required).length,
      current: readyItems.filter(i => !!items.find(item => item.required && item.field === i.field))
        .length
    },
    total: items.length,
    current: readyItems.length
  };
}

export default {
  item,
  progress,
  areEqual,
  getDefaultValue
};
